import { useEffect, useState } from "react"

import { api, type PecaNaTela } from "../api"
import { Button } from "../componentes/Button"
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "../componentes/Card"
import * as Icones from "../componentes/Icones"
import { Selo } from "../componentes/Selo"
import { Column } from "../componentes/TablePrincipal2"
import { ToggleGroup, ToggleGroupItem } from "../componentes/ToggleGroup"
import { dataHora, inteiro, plural } from "../formato"
import { CONTAGEM_DO_PAPEL, TEXTO_DO_CADASTRO } from "../lib/classes"
import { EstadoDoPedido, usePedido } from "../ficha/Pedido"
import { BotoesDaLinha, COLUNA_DE_ACOES, TabelaDaFicha, VazioDaFicha } from "../ficha/Tabela"

type PecaComPapel = PecaNaTela & { rotulo_do_papel: string }

const TODAS = "todas"

export function PecasDoProcesso({
  contratoId,
  aoAbrirProcesso,
  aoContarNoProcesso,
}: {
  contratoId: number
  aoAbrirProcesso: (pecaId?: number) => void
  aoContarNoProcesso?: (total: number) => void
}) {
  const pedido = usePedido(() => api.pecas(contratoId), `pecas-${contratoId}`)
  const [papel, definirPapel] = useState(TODAS)

  const total = pedido.dados?.total_no_processo ?? null
  useEffect(() => {
    if (total !== null) aoContarNoProcesso?.(total)
  }, [total, aoContarNoProcesso])

  const pecas = pedido.dados

  return (
    <Card>
      <CardHeader>
        <CardTitle>Peças do processo</CardTitle>
        {pecas !== null && (
          <CardAction>
            <span className="text-xs text-muted-foreground">
              {pecas.sincronizado_em === null
                ? "Processo ainda não lido do ATHUS"
                : `${plural(pecas.total_no_processo, "peça", "peças")}, ${inteiro(pecas.total)} de fiscalização · ` +
                  `lido do ATHUS em ${dataHora(pecas.sincronizado_em)}`}
            </span>
          </CardAction>
        )}
      </CardHeader>
      <CardContent>
        <EstadoDoPedido pedido={pedido} oQue="as peças">
          {(dados) => {
            const todas: PecaComPapel[] = dados.grupos.flatMap((g) => g.pecas.map((p) => ({ ...p, rotulo_do_papel: g.rotulo })))
            const escolhido = dados.grupos.some((g) => g.papel === papel) ? papel : TODAS
            const visiveis = escolhido === TODAS ? todas : todas.filter((p) => p.papel === escolhido)
            const abas = (
              <ToggleGroup
                type="single"
                aria-label="Papéis das peças"
                value={escolhido}
                onValueChange={(valor) => valor !== "" && definirPapel(valor)}
              >
                {[{ papel: TODAS, rotulo: "Todas", quantas: todas.length }, ...dados.grupos.map((g) => ({ papel: g.papel, rotulo: g.rotulo, quantas: g.pecas.length }))].map(
                  (opcao) => (
                    <ToggleGroupItem key={opcao.papel} value={opcao.papel}>
                      {opcao.rotulo}
                      <span className={CONTAGEM_DO_PAPEL}>{inteiro(opcao.quantas)}</span>
                    </ToggleGroupItem>
                  ),
                )}
              </ToggleGroup>
            )
            return (
              <TabelaDaFicha<PecaComPapel>
                dados={visiveis}
                ordenar={{ criado_na_origem_em: (p) => p.criado_na_origem_em }}
                abasNaBarra={todas.length > 0 ? abas : undefined}
                aoClicarNaLinha={(p) => aoAbrirProcesso(p.id)}
                vazio={
                  dados.total_no_processo === 0 ? (
                    <VazioDaFicha titulo="As peças deste processo ainda não foram trazidas do ATHUS" />
                  ) : (
                    <VazioDaFicha
                      titulo="Nenhuma peça deste processo está no catálogo de fiscalização"
                      descricao="O processo inteiro continua disponível na esteira."
                      acao={
                        <Button type="button" size="sm" onClick={() => aoAbrirProcesso()}>
                          <Icones.Abrir />
                          Abrir o processo inteiro
                        </Button>
                      }
                    />
                  )
                }
              >
                <Column<PecaComPapel>
                  name="titulo"
                  label="Peça"
                  render={(_v, p) => (
                    <span className={TEXTO_DO_CADASTRO}>
                      {p.titulo ?? "—"}
                      {p.numero !== null && ` · nº ${p.numero}`}
                    </span>
                  )}
                />
                <Column<PecaComPapel> name="rotulo_do_papel" label="Papel" render={(_v, p) => p.rotulo_do_papel} />
                <Column<PecaComPapel>
                  name="tipo_descricao"
                  label="Tipo"
                  render={(_v, p) => (
                    <span className={TEXTO_DO_CADASTRO}>
                      {p.tipo_descricao ?? `Tipo ${p.cd_tipo_documento}`}
                      {p.juntada === true && " · juntada"}
                    </span>
                  )}
                />
                <Column<PecaComPapel>
                  name="criado_na_origem_em"
                  label="Criada em"
                  render={(_v, p) => <span className="tabular-nums">{dataHora(p.criado_na_origem_em)}</span>}
                />
                <Column<PecaComPapel>
                  name="pdf_conferido_em"
                  label="Conferência"
                  render={(_v, p) =>
                    p.cancelada ? (
                      <Selo situacao="cancelada">Cancelada no processo</Selo>
                    ) : p.pdf_conferido_em !== null ? (
                      <Selo situacao="conferida">Conferido em {dataHora(p.pdf_conferido_em)}</Selo>
                    ) : (
                      <Selo situacao="nao_conferida" />
                    )
                  }
                />
                <Column<PecaComPapel>
                  name="acoes"
                  label="Ações"
                  classe={COLUNA_DE_ACOES}
                  render={(_v, p) => (
                    <BotoesDaLinha acoes={[{ rotulo: "Abrir no processo", icone: Icones.Abrir, aoClicar: () => aoAbrirProcesso(p.id) }]} />
                  )}
                />
              </TabelaDaFicha>
            )
          }}
        </EstadoDoPedido>
      </CardContent>
      {pecas !== null && pecas.total_no_processo > 0 && (
        <div className="flex flex-wrap items-center gap-3 border-t px-4 py-2.5">
          <Button type="button" size="sm" onClick={() => aoAbrirProcesso()}>
            <Icones.Abrir />
            Abrir o processo inteiro
          </Button>
          <span className="text-xs text-muted-foreground">
            {plural(pecas.total_no_processo - pecas.total, "peça do processo fica", "peças do processo ficam")} fora do catálogo de
            fiscalização: despacho, ofício, memorando e o que mais o processo tiver.
          </span>
        </div>
      )}
    </Card>
  )
}
